import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
} from "react-router-dom";

import RootLayout from "@/app/root-layout";
import { AuthProvider } from "@/components/auth-provider";
import { RouteErrorBoundary } from "@/components/error-boundary";
import {
  PUBLIC_ROUTER_PATHS,
  type PublicRouterPath,
} from "@/lib/public-routes";

/**
 * Wraps an authenticated route subtree in the auth provider. The impersonation
 * banner is mounted higher up in RootLayout so it also covers the public
 * auth-flow routes that render outside this layout.
 */
function AuthLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

export type Loader = () => Promise<{ default: React.ComponentType }>;

/** Lazy-load a page module via React Router's native `lazy` property. */
export function page(load: Loader) {
  return { lazy: () => load().then((m) => ({ Component: m.default })) };
}

// Page loader per public route pattern. Typed as Record<PublicRouterPath, …> so
// adding a path to PUBLIC_ROUTER_PATHS without a loader (or vice versa) is a
// compile error — the path list and the loaders cannot drift.
const PUBLIC_PAGE_LOADERS: Record<PublicRouterPath, Loader> = {
  login: () => import("@/app/login/page"),
  signup: () => import("@/app/signup/page"),
  "confirm-email/:id": () => import("@/app/confirm-email/[id]/page"),
  "forgot-password": () => import("@/app/forgot-password/page"),
  "password-reset-sent": () => import("@/app/password-reset-sent/page"),
  "recovery/:id": () => import("@/app/recovery/[id]/page"),
  "recovery/success": () => import("@/app/recovery/success/page"),
};

/**
 * Public / unauthenticated routes shared by the web and mobile trees:
 * landing, the auth flow, password recovery and token-based email
 * confirmation. These must NOT sit under AuthLayout — email-confirmation
 * links are followed by logged-out users, and AuthProvider would bounce
 * them to /login before the token is ever consumed.
 *
 * Built from PUBLIC_ROUTER_PATHS (the single source of truth shared with the
 * shell/permission gates) so the router and isPublicRoute can't drift.
 */
function publicRoutes(): RouteObject[] {
  return [
    { index: true, ...page(() => import("@/app/page")) },
    ...PUBLIC_ROUTER_PATHS.map((path) => ({
      path,
      ...page(PUBLIC_PAGE_LOADERS[path]),
    })),
  ];
}

/** The preferences route — flat single page with legacy redirects. */
export function preferencesRoute(): RouteObject {
  return {
    path: "preferences",
    children: [
      { index: true, ...page(() => import("@/app/preferences/page")) },
      // Legacy sub-routes redirect to the flat page so bookmarks don't 404.
      {
        path: "general",
        element: <Navigate to="/preferences" replace />,
      },
      {
        path: "sidebar",
        element: <Navigate to="/preferences" replace />,
      },
    ],
  };
}

/**
 * Assemble a data router from the shared shell that both trees use: the `/`
 * RootLayout root, the public/auth routes, the authenticated subtree (wrapped
 * in AuthLayout), any extra top-level routes (e.g. web's superadmin group),
 * and the catch-all 404. Centralizing the skeleton keeps web.tsx and
 * mobile.tsx down to just their route lists.
 */
export function buildAppRouter(
  authChildren: RouteObject[],
  extraRootChildren: RouteObject[] = [],
) {
  return createBrowserRouter([
    {
      path: "/",
      element: <RootLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        ...publicRoutes(),
        { element: <AuthLayout />, children: authChildren },
        ...extraRootChildren,
        { path: "*", ...page(() => import("@/app/not-found/page")) },
      ],
    },
  ]);
}
